import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, styled, Grid } from "@mui/material";
import OpsLinkFlights from "./OpsLinkFlights";
import OpsLinkHeader from "./OpsLinkHeader";
import OpsLinkTopics from "./OpsLinkTopics";
import OpsLinkEventList from "./OpsLinkEventList";
import OpsLinkEventDisplay from "./OpsLinkEventDisplay";
import OpsLinkCreateEvent from "./OpsLinkCreateEvent";
import { IOpsLinkMessage, IOpsLinkTopic, IFlight } from "@vmosolutions/oc-interfaces";
import { getEventsByFlight, getTopics, refreshFlightEvents } from "src/redux/reducers/OpsLinkReducer";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import WebsocketClient from "src/websockets/WebsocketClient";
import SplitterLayout from "react-splitter-layout";
import "react-splitter-layout/lib/index.css";

interface IOpsLinkWindow {
  id?: string;
  windowProps: {
    initialFlights?: IFlight[];
    /** Signal prop set by openOrFocusOpsLinkWindow to add a flight to an existing window. */
    flightToAdd?: { flight: IFlight; _ts: number };
  };
}

const WINDOW_TOP_OFFSET = 120;
const HEADER_ROW_HEIGHT = 50;

const OCOpsLinkWindow = styled(Box)(({ theme }) => ({
  width: "100vw",
  height: `calc(100vh - ${WINDOW_TOP_OFFSET}px)`,
}));

export const enum framePositions {
  BELOW = "BELOW",
  SIDE = "SIDE",
  POPUP = "POPUP",
}

export function OpsLinkWindow(props: IOpsLinkWindow): JSX.Element {
  const { id, windowProps } = props;
  const dispatch = useDispatch();
  const headerHeight = 80;

  //tracked flights are all flights that appear in the column list
  const [trackedFlights, setTrackedFlights] = useState<IFlight[]>([]);
  //selected flights are the tracked flights the user is currently checking
  const [selectedFlights, setSelectedFlights] = useState<IFlight[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<IOpsLinkTopic[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<IOpsLinkMessage[]>([]);
  const [replyEvent, setReplyEvent] = useState<IOpsLinkMessage>();
  const [currentEvent, setCurrentEvent] = useState<IOpsLinkMessage>();
  const [disabledPriorities, setDisabledPriorities] = useState<string[]>([]);
  const [searchVal, setSearchVal] = useState<string>("");
  const [framePos, setFramePos] = useState<framePositions>(framePositions.BELOW);
  const [initialFlights, setInitialFlights] = useState<IFlight[]>(windowProps?.initialFlights ?? []);

  const submissionTime = useSelector((state: IRootState) => state.OpsLinkReducer.submissionTime);
  const flightEvents = useSelector((state: IRootState) => state.OpsLinkReducer.flightEvents);
  const topics = useSelector((state: IRootState) => state.OpsLinkReducer.topicList);

  useEffect(() => {
    dispatch(getTopics());
  }, []);

  useEffect(() => {
    if (initialFlights) {
      setTrackedFlights(initialFlights);
      setSelectedFlights(initialFlights);
      initialFlights.forEach((flight) => {
        dispatch(getEventsByFlight({ flightId: flight._id }));
      });
    }
  }, []);

  // When a flightToAdd signal arrives (via openOrFocusOpsLinkWindow),
  // add the flight to tracked/selected lists if not already present.
  // Dedup is checked against local trackedFlights — the component's
  // source of truth — so untracked-then-re-added flights work correctly.
  useEffect(() => {
    if (windowProps?.flightToAdd) {
      const { flight } = windowProps.flightToAdd;
      const alreadyTracked = trackedFlights.some((tracked) => tracked._id === flight._id);
      if (!alreadyTracked) {
        setTrackedFlights((prev) => [...prev, flight]);
        setSelectedFlights((prev) => [...prev, flight]);
        dispatch(getEventsByFlight({ flightId: flight._id }));
      }
    }
  }, [windowProps?.flightToAdd, trackedFlights, dispatch]);

  // Subscribe to OpsLink updates for tracked flights via the main WebSocket.
  // The main WS handler (WebsocketHandlers.ts) already dispatches
  // getEventsByFlight on incoming OpsLinkMessage events, so Redux and the
  // counter update automatically — no per-window listener needed.
  useEffect(() => {
    trackedFlights.forEach((flight) => {
      WebsocketClient.send({ action: "subscribe", flightId: flight._id });
      dispatch(getEventsByFlight({ flightId: flight._id }));
    });
  }, [trackedFlights]);

  useEffect(() => {
    setSelectedTopics(topics);
  }, [topics]);

  useEffect(() => {
    const currentEvents: IOpsLinkMessage[] = [];
    if (selectedFlights.length === 0) {
      setSelectedTopics(topics);
    }
    selectedFlights.forEach((selectedFlight) => {
      const flightTopics = flightEvents[selectedFlight._id];
      selectedTopics.forEach((selectedTopic) => {
        if (flightTopics?.hasOwnProperty(selectedTopic._id)) {
          currentEvents.push(...flightTopics[selectedTopic._id]);
        }
      });
    });
    setSelectedEvents(currentEvents);
  }, [selectedTopics, flightEvents, selectedFlights]);

  useEffect(() => {
    if (submissionTime !== 0) {
      dispatch(refreshFlightEvents(false));
    }
  }, [submissionTime]);

  // Keep currentEvent in sync with refreshed data from Redux
  useEffect(() => {
    if (currentEvent && selectedEvents.length > 0) {
      const updated = selectedEvents.find((evt) => evt._id === currentEvent._id);
      if (updated) {
        setCurrentEvent(updated);
      }
    }
  }, [selectedEvents]);

  const clearFlights = useCallback(() => {
    setSelectedFlights([]);
    setTrackedFlights([]);
    setSelectedTopics([]);
    setSelectedEvents([]);
    setReplyEvent(null);
    setCurrentEvent(null);
    setDisabledPriorities([]);
    setSearchVal("");
  }, []);

  return (
    <OCOpsLinkWindow>
      <Grid container wrap="nowrap">
        <Grid item xs={"auto"} sx={{ width: "150px", height: "100%" }}>
          <OpsLinkFlights
            id={id}
            headerHeight={headerHeight}
            trackedFlights={trackedFlights}
            setTrackedFlights={setTrackedFlights}
            selectedFlights={selectedFlights}
            setSelectedFlights={setSelectedFlights}
          />
        </Grid>
        <Grid item xs sx={{ height: "100%" }}>
          <Grid container>
            <Grid item className="opsLinkHeader" xs={12} sx={{ height: `${HEADER_ROW_HEIGHT}px` }}>
              <OpsLinkHeader setCurrentEvent={setCurrentEvent} clearFlights={clearFlights} id={id} />
            </Grid>
            <Grid item xs={12} container wrap="nowrap" sx={{ height: "100%" }}>
              <Grid item className="topicContainer" xs={"auto"}>
                <OpsLinkTopics
                  id={id}
                  selectedFlights={selectedFlights}
                  headerHeight={headerHeight}
                  selectedTopics={selectedTopics}
                  setSelectedTopics={setSelectedTopics}
                />
              </Grid>
              <Grid
                item
                xs={10}
                sx={{ position: "relative", height: `calc(100vh - ${WINDOW_TOP_OFFSET + HEADER_ROW_HEIGHT}px)` }}
              >
                <SplitterLayout vertical percentage primaryMinSize={20} secondaryMinSize={15} secondaryInitialSize={33}>
                  <Box className="eventListParent" sx={{ width: "100%", height: "100%", padding: "0px 5px" }}>
                    <OpsLinkEventList
                      id={id}
                      headerHeight={headerHeight}
                      setCurrentEvent={setCurrentEvent}
                      setReplyEvent={setReplyEvent}
                      selectedEvents={selectedEvents}
                      disabledPriorities={disabledPriorities}
                      setDisabledPriorities={setDisabledPriorities}
                      searchVal={searchVal}
                      setSearchVal={setSearchVal}
                      framePos={framePos}
                      setFramePos={setFramePos}
                      trackedFlights={trackedFlights}
                    />
                  </Box>
                  <Box
                    className="eventDisplayParent"
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: 0,
                      padding: "0px 5px",
                      overflow: "auto",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {currentEvent && !replyEvent ? (
                      <OpsLinkEventDisplay
                        id={id}
                        currentEvent={currentEvent}
                        setReplyEvent={setReplyEvent}
                        trackedFlights={trackedFlights}
                      />
                    ) : (
                      <OpsLinkCreateEvent id={id} replyEvent={replyEvent} trackedFlights={trackedFlights} />
                    )}
                  </Box>
                </SplitterLayout>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </OCOpsLinkWindow>
  );
}
